import { Check } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { TOKEN_ICONS } from '@/components/ui/token-icons';

/**
 * Floating product-UI mockups used in the hero's motion graphic (and reused in
 * the "Open app" dialog's MLS card). Purely presentational; each takes a
 * `className` for positioning / float animation by the caller.
 */

/** A treasury balance card with a few token rows. */
export function BalanceCard({ className }: { className?: string }) {
  const tokens = [
    { name: 'USDC', sub: 'USD Coin', amount: '$31.84M' },
    { name: 'ETH', sub: 'Ethereum', amount: '$12.10M' },
    { name: 'WBTC', sub: 'Wrapped BTC', amount: '$4.35M' },
  ];
  return (
    <div
      className={`rounded-2xl border border-hairline bg-white px-8 py-7 shadow-lift ${className ?? ''}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-400">
          Treasury balance
        </span>
        <Badge variant="success" className="px-2 py-0.5 text-[0.65rem]">
          +2.4%
        </Badge>
      </div>
      <div className="mt-1.5 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.75rem]">
        $48.29M
      </div>
      <div className="mt-4 space-y-3 border-t border-hairline pt-4">
        {tokens.map((t) => {
          const Icon = TOKEN_ICONS[t.name];
          return (
            <div key={t.name} className="flex items-center gap-2.5">
              {Icon && <Icon className="size-6 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium leading-tight text-slate-900">
                  {t.name}
                </div>
                <div className="text-[0.7rem] leading-tight text-ink-400">
                  {t.sub}
                </div>
              </div>
              <span className="text-sm font-medium text-ink-700">
                {t.amount}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A pending-approval card with signer avatars and an Approve action. */
export function ApprovalCard({ className }: { className?: string }) {
  return (
    <div
      className={`rounded-2xl border border-hairline bg-white px-8 py-7 shadow-lift ${className ?? ''}`}
    >
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-ember-500" />
        <span className="text-xs font-medium text-ink-500">
          Pending approval
        </span>
      </div>
      <div className="mt-3">
        <div className="text-lg font-semibold tracking-tight text-slate-900">
          2,500,000 <span className="text-ink-400">USDC</span>
        </div>
        <div className="font-mono text-[0.7rem] text-ink-400">
          to Treasury · 0x4f…a21
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-hairline pt-4">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            <span className="size-6 rounded-full border-2 border-white bg-ink-300" />
            <span className="size-6 rounded-full border-2 border-white bg-ink-300" />
            <span className="grid size-6 place-items-center rounded-full border-2 border-dashed border-ink-200 bg-white text-[0.6rem] text-ink-400">
              ?
            </span>
          </div>
          <span className="text-xs font-medium text-ink-500">2 of 3</span>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium text-white">
          <Check className="size-3.5" />
          Approve
        </span>
      </div>
    </div>
  );
}
